<template>
  <NcrmRecordEditDialog
    :model-value="modelValue"
    :title="drawerTitle"
    :subtitle="node?.description || ''"
    :save-label="$t('app.actions.save')"
    :save-disabled="saveDisabled"
    :max-width="isDuplicateCheckNode || isDataEnrichmentNode || isLeadBoardAddNode || isExternalFormNode ? 860 : 620"
    @update:model-value="$emit('update:modelValue', $event)"
    @save="handleSave"
  >
    <div v-if="isExternalFormNode" class="d-flex flex-column ga-4">
      <NcrmWorkflowExternalFormsSettings
        :workflow-instance-id="workflowInstanceId"
        :endpoints="endpoints"
        :select-menu-props="selectMenuProps"
      />
    </div>

    <div v-else-if="isApiNode" class="d-flex flex-column ga-4">
      <v-alert type="info">
        {{ $t("ncrm.workflows.nodeSettings.apiBestPractice") }}
      </v-alert>

      <v-select
        v-model="form.endpointId"
        :items="endpointOptions"
        item-title="label"
        item-value="value"
        :label="$t('ncrm.workflows.nodeSettings.apiSource')"
        hide-details="auto"
        :loading="loadingEndpoints"
        :menu-props="selectMenuProps"
      />

      <div class="d-flex flex-wrap ga-2">
        <v-btn color="primary" variant="tonal" @click="$emit('open-external-api')">
          <v-icon start>mdi-open-in-new</v-icon>
          {{ $t("ncrm.workflows.actions.openExternalApi") }}
        </v-btn>
      </div>

      <v-row dense>
        <v-col cols="12" md="6">
          <v-select
            v-model="form.entityScope"
            :items="entityScopeOptions"
            item-title="label"
            item-value="value"
            :label="$t('ncrm.workflows.nodeSettings.entityScope')"
            hide-details="auto"
            :menu-props="selectMenuProps"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            v-model="form.payloadFormat"
            :items="payloadFormatOptions"
            item-title="label"
            item-value="value"
            :label="$t('ncrm.workflows.nodeSettings.payloadFormat')"
            hide-details="auto"
            :menu-props="selectMenuProps"
          />
        </v-col>
      </v-row>

      <v-switch
        v-model="form.status"
        :true-value="'active'"
        :false-value="'inactive'"
        color="primary"
        hide-details
        :label="$t('ncrm.workflows.nodeSettings.active')"
      />

      <v-textarea
        v-model="form.description"
        :label="$t('ncrm.workflows.nodeSettings.description')"
        rows="3"
        hide-details="auto"
      />

      <v-card v-if="selectedEndpoint" variant="tonal" color="neutral" class="pa-3">
        <div class="text-caption text-medium-emphasis mb-2">
          {{ $t("ncrm.workflows.nodeSettings.selectedEndpoint") }}
        </div>
        <div class="text-body-2 font-weight-medium">{{ selectedEndpoint.name }}</div>
        <div class="text-caption text-medium-emphasis mt-1">
          {{ selectedEndpoint.endpoint_key }} · {{ selectedEndpoint.entity_scope }} · {{ selectedEndpoint.status }}
        </div>
      </v-card>

      <!-- ── Auto-sync worker ──────────────────────────────────────────────── -->
      <v-divider />
      <NcrmWorkflowAutoSyncSettings
        :form="form"
        :interval-options="intervalOptions"
        :menu-props="selectMenuProps"
      />

      <!-- Sprint 6b — Telegram notification on new API leads -->
      <v-divider />
      <NcrmWorkflowTelegramNotify
        :config="form.telegram"
        :title="$t('ncrm.workflows.nodeSettings.apiImport.telegramTitle')"
        :hint="$t('ncrm.workflows.nodeSettings.apiImport.telegramHint')"
      />
    </div>

    <div v-else-if="isDuplicateCheckNode" class="d-flex flex-column ga-4">
      <v-alert type="info">
        {{ $t("ncrm.workflows.nodeSettings.dedup.bestPractice") }}
      </v-alert>

      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field
            v-model.number="form.duplicateThreshold"
            type="number"
            min="0"
            max="100"
            suffix="%"
            :label="$t('ncrm.workflows.nodeSettings.dedup.duplicateThreshold')"
            hide-details="auto"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model.number="form.newRecordThreshold"
            type="number"
            min="0"
            max="100"
            suffix="%"
            :label="$t('ncrm.workflows.nodeSettings.dedup.newRecordThreshold')"
            hide-details="auto"
          />
        </v-col>
      </v-row>

      <v-alert v-if="thresholdError" type="warning" density="compact">
        {{ thresholdError }}
      </v-alert>

      <v-switch
        v-model="form.skipAiVerification"
        color="warning"
        hide-details
        :label="$t('ncrm.workflows.nodeSettings.dedup.skipAiVerification')"
      />
      <v-alert v-if="form.skipAiVerification" type="warning" density="compact">
        {{ $t("ncrm.workflows.nodeSettings.dedup.skipAiVerificationHint") }}
      </v-alert>

      <v-divider />
      <div class="d-flex align-center justify-space-between flex-wrap ga-2">
        <div>
          <div class="text-body-2 font-weight-semibold ncrm-text-strong">
            {{ $t("ncrm.workflows.nodeSettings.dedup.fieldsTitle") }}
          </div>
          <div class="text-caption text-medium-emphasis mt-1">
            {{ $t("ncrm.workflows.nodeSettings.dedup.fieldsHint") }}
          </div>
        </div>
        <v-chip :color="weightTotal === 100 ? 'success' : 'warning'">
          {{ $t("ncrm.workflows.nodeSettings.dedup.weightTotal", { total: weightTotal }) }}
        </v-chip>
      </div>

      <div class="d-flex flex-column ga-3">
        <v-card
          v-for="(field, index) in form.matchFields"
          :key="field.id || index"
          variant="outlined"
          class="pa-3"
        >
          <div class="d-flex align-start ga-2">
            <v-row dense class="flex-grow-1">
              <v-col cols="12" md="3">
                <v-select
                  v-model="field.stagingField"
                  :items="dedupFieldOptions"
                  item-title="label"
                  item-value="value"
                  :label="$t('ncrm.workflows.nodeSettings.dedup.stagingField')"
                  density="compact"
                  hide-details="auto"
                  :menu-props="selectMenuProps"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="field.companyField"
                  :items="dedupFieldOptions"
                  item-title="label"
                  item-value="value"
                  :label="$t('ncrm.workflows.nodeSettings.dedup.companyField')"
                  density="compact"
                  hide-details="auto"
                  :menu-props="selectMenuProps"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="field.method"
                  :items="dedupMethodOptions"
                  item-title="label"
                  item-value="value"
                  :label="$t('ncrm.workflows.nodeSettings.dedup.method')"
                  density="compact"
                  hide-details="auto"
                  :menu-props="selectMenuProps"
                />
              </v-col>
              <v-col cols="8" md="2">
                <v-text-field
                  v-model.number="field.weight"
                  type="number"
                  min="0"
                  max="100"
                  suffix="%"
                  :label="$t('ncrm.workflows.nodeSettings.dedup.weight')"
                  density="compact"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="4" md="1" class="d-flex align-center justify-end">
                <v-btn
                  icon="mdi-delete-outline"
                  variant="text"
                  color="error"
                  size="small"
                  :disabled="form.matchFields.length <= 1"
                  :aria-label="$t('app.actions.delete')"
                  @click="removeDedupField(index)"
                />
              </v-col>
              <v-col cols="12">
                <v-checkbox
                  v-model="field.forceExactScore"
                  density="compact"
                  hide-details
                  :label="$t('ncrm.workflows.nodeSettings.dedup.forceExactScore')"
                />
              </v-col>
            </v-row>
          </div>
        </v-card>
      </div>

      <v-btn color="primary" variant="tonal" class="align-self-start" @click="addDedupField">
        <v-icon start>mdi-plus</v-icon>
        {{ $t("ncrm.workflows.nodeSettings.dedup.addField") }}
      </v-btn>

      <v-alert v-if="weightTotal !== 100" type="warning" density="compact">
        {{ $t("ncrm.workflows.nodeSettings.dedup.weightError") }}
      </v-alert>

      <v-divider />
      <div class="d-flex flex-column ga-3">
        <div>
          <div class="text-body-2 font-weight-semibold ncrm-text-strong">
            {{ $t("ncrm.workflows.nodeSettings.dedup.autoRunTitle") }}
          </div>
          <div class="text-caption text-medium-emphasis mt-1">
            {{ $t("ncrm.workflows.nodeSettings.dedup.autoRunHint") }}
          </div>
        </div>

        <v-switch
          v-model="form.autoRun"
          color="primary"
          hide-details
          :label="form.autoRun
            ? $t('ncrm.workflows.nodeSettings.autoSyncOn')
            : $t('ncrm.workflows.nodeSettings.autoSyncOff')"
        />

        <v-select
          v-if="form.autoRun"
          v-model="form.autoRunIntervalMinutes"
          :items="dedupIntervalOptions"
          item-title="label"
          item-value="value"
          :label="$t('ncrm.workflows.nodeSettings.dedup.autoRunInterval')"
          hide-details="auto"
          :menu-props="selectMenuProps"
        />
      </div>

      <v-textarea
        v-model="form.description"
        :label="$t('ncrm.workflows.nodeSettings.description')"
        rows="2"
        hide-details="auto"
      />
    </div>

    <div v-else-if="isFileImportNode" class="d-flex flex-column ga-4">
      <v-alert type="info">
        {{ $t("ncrm.workflows.nodeSettings.fileImport.bestPractice") }}
      </v-alert>

      <v-switch
        v-model="form.status"
        :true-value="'active'"
        :false-value="'inactive'"
        color="primary"
        hide-details
        :label="$t('ncrm.workflows.nodeSettings.active')"
      />

      <v-textarea
        v-model="form.description"
        :label="$t('ncrm.workflows.nodeSettings.description')"
        rows="3"
        hide-details="auto"
      />

      <v-divider />

      <div class="d-flex flex-column ga-3">
        <div>
          <div class="text-body-2 font-weight-semibold ncrm-text-strong">
            {{ $t("ncrm.workflows.nodeSettings.fileImport.autoDedupTitle") }}
          </div>
          <div class="text-caption text-medium-emphasis mt-1">
            {{ $t("ncrm.workflows.nodeSettings.fileImport.autoDedupHint") }}
          </div>
        </div>

        <v-switch
          v-model="form.autoDedupAfterImport"
          color="primary"
          hide-details
          :label="form.autoDedupAfterImport
            ? $t('ncrm.workflows.nodeSettings.fileImport.autoDedupOn')
            : $t('ncrm.workflows.nodeSettings.fileImport.autoDedupOff')"
        />
      </div>

      <!-- ── Istoric upload-uri ─────────────────────────────────────────── -->
      <v-divider />
      <NcrmWorkflowFileImportHistory :workflow-instance-id="workflowInstanceId" />
    </div>

    <NcrmWorkflowDataEnrichmentSettings
      v-else-if="isDataEnrichmentNode"
      :form="form"
      :select-menu-props="selectMenuProps"
      :interval-options="intervalOptions"
      :enrichment-mode-options="enrichmentModeOptions"
      :prompt-options="promptOptions"
      :selected-prompt="selectedPrompt"
      :selected-prompt-meta="selectedPromptMeta"
      :target-field-options="targetFieldOptions"
      :enrichment-error="enrichmentError"
      :loading-ai-prompts="loadingAiPrompts"
      :ai-prompts-error="aiPromptsError"
      @open-ai-workspace="$emit('open-ai-workspace')"
      @refresh-ai-prompts="$emit('refresh-ai-prompts')"
      @apply-selected-prompt-profile="applySelectedPromptProfile"
    />

    <NcrmWorkflowCrmWriteSettings
      v-else-if="isCrmWriteNode"
      :form="form"
      :interval-options="intervalOptions"
      :select-menu-props="selectMenuProps"
    />

    <NcrmWorkflowLeadBoardSettings
      v-else-if="isLeadBoardAddNode"
      :form="form"
      :interval-options="intervalOptions"
      :select-menu-props="selectMenuProps"
      :lead-board-options="leadBoardOptions"
      :lead-stage-options="leadStageOptions"
      :loading-lead-boards="loadingLeadBoards"
      :loading-lead-stages="loadingLeadStages"
      :lead-board-error="leadBoardError"
      @fetch-lead-stages="fetchLeadStages"
    />

    <NcrmWorkflowMailjetTriggerSettings
      v-else-if="isMailjetTriggerNode"
      :settings="settings"
      :interval-options="intervalOptions"
      :select-menu-props="selectMenuProps"
      @update:value="mailjetTriggerForm = $event"
    />

    <NcrmWorkflowAiCompatibilitySettings
      v-else-if="isAiCompatibilityNode"
      :settings="settings"
      :ai-prompts="aiPrompts"
      :loading-ai-prompts="loadingAiPrompts"
      :select-menu-props="selectMenuProps"
      @update:value="aiCompatibilityForm = $event"
      @open-ai-workspace="$emit('open-ai-workspace')"
    />

    <NcrmWorkflowMailjetLeadAddSettings
      v-else-if="isMailjetLeadAddNode"
      :settings="settings"
      :interval-options="intervalOptions"
      :select-menu-props="selectMenuProps"
      @update:value="mailjetLeadAddForm = $event"
    />

    <NcrmWorkflowMailjetTelegramSettings
      v-else-if="isTelegramNotifyNode"
      :settings="settings"
      :select-menu-props="selectMenuProps"
      @update:value="mailjetTelegramForm = $event"
    />

    <div v-else-if="isMailjetPipelineNode" class="d-flex flex-column ga-4">
      <v-alert type="info" density="comfortable">
        {{ $t("ncrm.workflows.nodeSettings.mailjetPipeline.hint") }}
      </v-alert>
      <v-switch
        v-model="form.status"
        :true-value="'active'"
        :false-value="'inactive'"
        color="primary"
        hide-details
        :label="$t('ncrm.workflows.nodeSettings.active')"
      />
      <div class="text-caption text-medium-emphasis">
        {{ $t("ncrm.workflows.nodeSettings.mailjetPipeline.statusHint") }}
      </div>
    </div>

    <v-alert v-else type="info">
      {{ $t("ncrm.workflows.nodeSettings.futureNode") }}
    </v-alert>
  </NcrmRecordEditDialog>
</template>

<script>
import axios from "axios";
import NcrmWorkflowCrmWriteSettings from "@/components/ncrm/workflows/NcrmWorkflowCrmWriteSettings.vue";
import NcrmWorkflowDataEnrichmentSettings from "@/components/ncrm/workflows/NcrmWorkflowDataEnrichmentSettings.vue";
import NcrmWorkflowExternalFormsSettings from "@/components/ncrm/workflows/NcrmWorkflowExternalFormsSettings.vue";
import NcrmWorkflowLeadBoardSettings from "@/components/ncrm/workflows/NcrmWorkflowLeadBoardSettings.vue";
import NcrmWorkflowMailjetTriggerSettings from "@/components/ncrm/workflows/NcrmWorkflowMailjetTriggerSettings.vue";
import NcrmWorkflowAiCompatibilitySettings from "@/components/ncrm/workflows/NcrmWorkflowAiCompatibilitySettings.vue";
import NcrmWorkflowMailjetLeadAddSettings from "@/components/ncrm/workflows/NcrmWorkflowMailjetLeadAddSettings.vue";
import NcrmWorkflowMailjetTelegramSettings from "@/components/ncrm/workflows/NcrmWorkflowMailjetTelegramSettings.vue";
import NcrmWorkflowFileImportHistory from "@/components/ncrm/workflows/NcrmWorkflowFileImportHistory.vue";
import NcrmWorkflowAutoSyncSettings from "@/components/ncrm/workflows/NcrmWorkflowAutoSyncSettings.vue";
import NcrmWorkflowTelegramNotify from "@/components/ncrm/workflows/NcrmWorkflowTelegramNotify.vue";
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import {
  WORKFLOW_NODE_KEYS,
  AUTO_SYNC_INTERVAL_OPTIONS,
  AUTO_SYNC_DEFAULT_INTERVAL_MINUTES,
  DEDUP_FIELD_OPTIONS,
  DEDUP_METHOD_OPTIONS,
  DATA_ENRICHMENT_TARGET_FIELD_OPTIONS,
  createDefaultDataEnrichmentSettings,
  createDefaultDuplicateCheckSettings,
  createDefaultCrmWriteSettings,
  createDefaultLeadBoardAddSettings,
} from "@/Utile/ncrmWorkflows.mjs";
import { listStagingFileImports } from "@/Utile/ncrmStagingFileImportApi.mjs";

export default {
  name: "NcrmWorkflowNodeSettingsDrawer",
  components: {
    NcrmRecordEditDialog,
    NcrmWorkflowCrmWriteSettings,
    NcrmWorkflowDataEnrichmentSettings,
    NcrmWorkflowExternalFormsSettings,
    NcrmWorkflowLeadBoardSettings,
    NcrmWorkflowMailjetTriggerSettings,
    NcrmWorkflowAiCompatibilitySettings,
    NcrmWorkflowMailjetLeadAddSettings,
    NcrmWorkflowMailjetTelegramSettings,
    NcrmWorkflowFileImportHistory,
    NcrmWorkflowAutoSyncSettings,
    NcrmWorkflowTelegramNotify,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    node: {
      type: Object,
      default: null,
    },
    settings: {
      type: Object,
      default: () => ({}),
    },
    endpoints: {
      type: Array,
      default: () => [],
    },
    loadingEndpoints: {
      type: Boolean,
      default: false,
    },
    /**
     * Necesar pentru ramura File Import — drawer-ul își încarcă singur
     * istoricul de batch-uri când e deschis pe acest nod.
     */
    workflowInstanceId: {
      type: String,
      default: null,
    },
    aiPrompts: {
      type: Array,
      default: () => [],
    },
    loadingAiPrompts: {
      type: Boolean,
      default: false,
    },
    aiPromptsError: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue", "save", "open-external-api", "open-ai-workspace", "refresh-ai-prompts"],
  data() {
    return {
      form: {
        endpointId: null,
        status: "active",
        entityScope: "inherit",
        payloadFormat: "json",
        description: "",
        autoSync: false,
        autoSyncIntervalMinutes: AUTO_SYNC_DEFAULT_INTERVAL_MINUTES,
        duplicateThreshold: 65,
        newRecordThreshold: 20,
        autoRun: false,
        autoRunIntervalMinutes: AUTO_SYNC_DEFAULT_INTERVAL_MINUTES,
        skipAiVerification: false,
        matchFields: [],
        // File import node
        autoDedupAfterImport: false,
        // Data enrichment node
        mode: "manual",
        promptKey: "ncrm_company_enrichment",
        aiProfileKey: "enrich_research",
        targetFields: [],
        rerunDedupAfterEnrichment: true,
        maxRowsPerRun: 25,
        // Lead board node
        boardId: null,
        stageId: null,
      },
      selectMenuProps: { maxHeight: 320, zIndex: 2710 },
      mailjetTriggerForm: null,
      aiCompatibilityForm: null,
      mailjetLeadAddForm: null,
      mailjetTelegramForm: null,
      leadBoards: [],
      leadStages: [],
      loadingLeadBoards: false,
      loadingLeadStages: false,
      leadBoardLoadError: "",
    };
  },
  computed: {
    isApiNode() {
      return this.node?.key === WORKFLOW_NODE_KEYS.API_IMPORT;
    },
    isExternalFormNode() {
      return this.node?.key === WORKFLOW_NODE_KEYS.EXTERNAL_FORM_WIDGET;
    },
    isFileImportNode() {
      return this.node?.key === WORKFLOW_NODE_KEYS.FILE_IMPORT;
    },
    isDuplicateCheckNode() {
      return this.node?.key === WORKFLOW_NODE_KEYS.DUPLICATE_CHECK;
    },
    isDataEnrichmentNode() {
      return this.node?.key === WORKFLOW_NODE_KEYS.DATA_ENRICHMENT;
    },
    isCrmWriteNode() {
      return this.node?.key === WORKFLOW_NODE_KEYS.CRM_WRITE;
    },
    isLeadBoardAddNode() {
      return this.node?.key === WORKFLOW_NODE_KEYS.LEAD_BOARD_ADD;
    },
    isMailjetTriggerNode() {
      return this.node?.key === WORKFLOW_NODE_KEYS.MAILJET_EVENT_TRIGGER;
    },
    isAiCompatibilityNode() {
      return this.node?.key === WORKFLOW_NODE_KEYS.AI_COMPATIBILITY_CHECK;
    },
    isMailjetLeadAddNode() {
      return this.node?.key === WORKFLOW_NODE_KEYS.MAILJET_LEAD_ADD;
    },
    isTelegramNotifyNode() {
      return this.node?.key === WORKFLOW_NODE_KEYS.TELEGRAM_NOTIFY;
    },
    isMailjetPipelineNode() {
      return [WORKFLOW_NODE_KEYS.CONTACT_EXISTENCE_CHECK, WORKFLOW_NODE_KEYS.LEAD_PRESENCE_CHECK]
        .includes(this.node?.key);
    },
    drawerTitle() {
      return this.node?.label || this.$t("ncrm.workflows.nodeSettings.title");
    },
    endpointOptions() {
      return this.endpoints.map((endpoint) => ({
        value: endpoint.id,
        label: `${endpoint.name || endpoint.endpoint_key} · ${endpoint.entity_scope || "-"}`,
      }));
    },
    selectedEndpoint() {
      return this.endpoints.find((endpoint) => endpoint.id === Number(this.form.endpointId)) || null;
    },
    entityScopeOptions() {
      return [
        { label: this.$t("ncrm.workflows.nodeSettings.entityScopes.inherit"), value: "inherit" },
        { label: this.$t("ncrm.workflows.nodeSettings.entityScopes.company"), value: "company" },
        { label: this.$t("ncrm.workflows.nodeSettings.entityScopes.person"), value: "person" },
        { label: this.$t("ncrm.workflows.nodeSettings.entityScopes.mixed"), value: "mixed" },
      ];
    },
    payloadFormatOptions() {
      return [{ label: "JSON", value: "json" }];
    },
    intervalOptions() {
      return AUTO_SYNC_INTERVAL_OPTIONS.map((opt) => ({
        value: opt.minutes,
        label: this.$t(opt.labelKey),
      }));
    },
    leadBoardOptions() {
      return this.leadBoards.map((board) => ({
        value: board.id,
        label: board.name,
      }));
    },
    leadStageOptions() {
      return this.leadStages.map((stage) => ({
        value: stage.id,
        label: stage.name,
      }));
    },
    dedupIntervalOptions() {
      return [{ value: 0, label: this.$t("ncrm.workflows.nodeSettings.dedup.autoRunImmediate") }, ...this.intervalOptions];
    },
    dedupFieldOptions() { return DEDUP_FIELD_OPTIONS; },
    dedupMethodOptions() {
      return DEDUP_METHOD_OPTIONS.map((opt) => ({
        value: opt.value,
        label: this.$t(opt.labelKey),
      }));
    },
    enrichmentModeOptions() {
      return [
        { value: "manual", label: this.$t("ncrm.workflows.nodeSettings.enrichment.modes.manual") },
        { value: "automatic", label: this.$t("ncrm.workflows.nodeSettings.enrichment.modes.automatic") },
      ];
    },
    promptOptions() {
      const options = (this.aiPrompts || []).map((prompt) => ({
        value: prompt.prompt_key,
        label: [
          prompt.display_name || prompt.prompt_key,
          prompt.model_key || "-",
          this.promptWebSearchLabel(prompt),
        ].join(" · "),
      }));
      if (this.form.promptKey && !options.some((option) => option.value === this.form.promptKey)) {
        options.unshift({ value: this.form.promptKey, label: this.form.promptKey });
      }
      return options;
    },
    selectedPrompt() {
      return (this.aiPrompts || []).find((prompt) => prompt.prompt_key === this.form.promptKey) || null;
    },
    selectedPromptMeta() {
      if (!this.selectedPrompt) return [];
      const webSearchEnabled = this.promptUsesWebSearch(this.selectedPrompt);
      return [
        {
          key: "profile",
          label: this.selectedPrompt.profile_key || this.form.aiProfileKey || "-",
          color: "neutral",
        },
        {
          key: "provider",
          label: this.selectedPrompt.provider_key || "-",
          color: "neutral",
        },
        {
          key: "model",
          label: this.selectedPrompt.model_key || "-",
          color: webSearchEnabled ? "info" : "neutral",
        },
        {
          key: "web-search",
          label: this.promptWebSearchLabel(this.selectedPrompt),
          color: webSearchEnabled ? "success" : "neutral",
        },
      ];
    },
    targetFieldOptions() {
      return DATA_ENRICHMENT_TARGET_FIELD_OPTIONS;
    },
    weightTotal() {
      return (this.form.matchFields || []).reduce((sum, field) => sum + Number(field.weight || 0), 0);
    },
    thresholdError() {
      if (!this.isDuplicateCheckNode) return "";
      if (Number(this.form.duplicateThreshold) <= Number(this.form.newRecordThreshold)) {
        return this.$t("ncrm.workflows.nodeSettings.dedup.thresholdError");
      }
      return "";
    },
    enrichmentError() {
      if (!this.isDataEnrichmentNode) return "";
      if (!String(this.form.promptKey || "").trim()) {
        return this.$t("ncrm.workflows.nodeSettings.enrichment.promptRequired");
      }
      if (!Array.isArray(this.form.targetFields) || !this.form.targetFields.length) {
        return this.$t("ncrm.workflows.nodeSettings.enrichment.targetFieldsRequired");
      }
      const limit = Number(this.form.maxRowsPerRun);
      if (!Number.isFinite(limit) || limit < 1 || limit > 100) {
        return this.$t("ncrm.workflows.nodeSettings.enrichment.batchLimitError");
      }
      return "";
    },
    leadBoardError() {
      if (!this.isLeadBoardAddNode) return "";
      if (this.leadBoardLoadError) return this.leadBoardLoadError;
      if (this.form.status !== "active" && !this.form.autoRun) return "";
      if (!this.form.boardId) return this.$t("ncrm.workflows.nodeSettings.leadBoard.boardRequired");
      if (!this.form.stageId) return this.$t("ncrm.workflows.nodeSettings.leadBoard.stageRequired");
      return "";
    },
    saveDisabled() {
      if (this.isExternalFormNode) return true;
      if (this.isDataEnrichmentNode) return Boolean(this.enrichmentError);
      if (this.isLeadBoardAddNode) return Boolean(this.leadBoardError);
      if (!this.isDuplicateCheckNode) return false;
      return this.weightTotal !== 100 || Boolean(this.thresholdError);
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.resetForm();
          if (this.isLeadBoardAddNode) this.fetchLeadBoards();
        }
      },
    },
    settings: {
      deep: true,
      handler() {
        if (this.modelValue) this.resetForm();
      },
    },
    node: {
      handler() {
        if (this.modelValue && this.isLeadBoardAddNode) {
          this.fetchLeadBoards();
        }
      },
    },
  },
  methods: {
    resetForm() {
      if (this.isDuplicateCheckNode) {
        const defaults = createDefaultDuplicateCheckSettings();
        const settings = {
          ...defaults,
          ...(this.settings || {}),
          matchFields: Array.isArray(this.settings?.matchFields) && this.settings.matchFields.length
            ? this.settings.matchFields
            : defaults.matchFields,
        };
        this.form = {
          ...this.form,
          ...settings,
          matchFields: settings.matchFields.map((field) => ({ ...field })),
        };
        return;
      }

      if (this.isFileImportNode) {
        this.form = {
          ...this.form,
          status: this.settings.status || "active",
          description: this.settings.description || "",
          autoDedupAfterImport: this.settings.autoDedupAfterImport ?? false,
        };
        return;
      }

      if (this.isDataEnrichmentNode) {
        const defaults = createDefaultDataEnrichmentSettings();
        const settings = {
          ...defaults,
          ...(this.settings || {}),
          targetFields: Array.isArray(this.settings?.targetFields) && this.settings.targetFields.length
            ? this.settings.targetFields
            : defaults.targetFields,
        };
        this.form = {
          ...this.form,
          ...settings,
          targetFields: [...settings.targetFields],
        };
        this.applySelectedPromptProfile();
        return;
      }

      if (this.isCrmWriteNode) {
        const defaults = createDefaultCrmWriteSettings();
        const settings = { ...defaults, ...(this.settings || {}) };
        this.form = {
          ...this.form,
          ...settings,
        };
        return;
      }

      if (this.isLeadBoardAddNode) {
        const defaults = createDefaultLeadBoardAddSettings();
        const settings = { ...defaults, ...(this.settings || {}) };
        this.form = {
          ...this.form,
          ...settings,
        };
        if (settings.boardId) this.fetchLeadStages(settings.boardId);
        return;
      }

      this.form = {
        endpointId: this.settings.endpointId || this.endpoints[0]?.id || null,
        status: this.settings.status || "active",
        entityScope: this.settings.entityScope || "inherit",
        payloadFormat: this.settings.payloadFormat || "json",
        description: this.settings.description || "",
        autoSync: this.settings.autoSync ?? false,
        autoSyncIntervalMinutes:
          this.settings.autoSyncIntervalMinutes ?? AUTO_SYNC_DEFAULT_INTERVAL_MINUTES,
        // Sprint 6b — config notificare Telegram pe lead nou via API.
        telegram: {
          enabled: !!this.settings.telegram?.enabled,
          recipientUserIds: Array.isArray(this.settings.telegram?.recipientUserIds)
            ? this.settings.telegram.recipientUserIds
            : [],
        },
      };
    },
    addDedupField() {
      const index = this.form.matchFields.length + 1;
      this.form.matchFields.push({
        id: `custom-${Date.now()}-${index}`,
        label: "",
        stagingField: "name",
        companyField: "name",
        method: "auto",
        weight: 0,
        forceExactScore: false,
      });
    },
    removeDedupField(index) {
      if (this.form.matchFields.length <= 1) return;
      this.form.matchFields.splice(index, 1);
    },
    applySelectedPromptProfile() {
      if (!this.isDataEnrichmentNode) return;
      const prompt = this.selectedPrompt;
      if (prompt?.profile_key) {
        this.form.aiProfileKey = prompt.profile_key;
      }
    },
    promptUsesWebSearch(prompt) {
      const config = prompt?.tool_config_json;
      if (!config || typeof config !== "object") return false;
      return config.web_search === true || config.web_search === "true" || config.web_search === 1;
    },
    promptWebSearchLabel(prompt) {
      return this.promptUsesWebSearch(prompt)
        ? this.$t("ncrm.workflows.nodeSettings.enrichment.webSearchOn")
        : this.$t("ncrm.workflows.nodeSettings.enrichment.webSearchOff");
    },
    handleSave() {
      if (this.saveDisabled) return;
      if (this.isMailjetTriggerNode) {
        this.$emit("save", this.mailjetTriggerForm || {});
        return;
      }
      if (this.isAiCompatibilityNode) {
        this.$emit("save", this.aiCompatibilityForm || {});
        return;
      }
      if (this.isMailjetLeadAddNode) {
        this.$emit("save", this.mailjetLeadAddForm || {});
        return;
      }
      if (this.isTelegramNotifyNode) {
        this.$emit("save", this.mailjetTelegramForm || {});
        return;
      }
      if (this.isMailjetPipelineNode) {
        this.$emit("save", { status: this.form.status || "active" });
        return;
      }
      if (this.isCrmWriteNode) {
        this.$emit("save", {
          status: this.form.status || "active",
          description: this.form.description || "",
          autoRun: Boolean(this.form.autoRun),
          autoRunIntervalMinutes: Number(this.form.autoRunIntervalMinutes || AUTO_SYNC_DEFAULT_INTERVAL_MINUTES),
        });
        return;
      }
      if (this.isLeadBoardAddNode) {
        this.$emit("save", {
          status: this.form.status || "inactive",
          description: this.form.description || "",
          autoRun: Boolean(this.form.autoRun),
          autoRunIntervalMinutes: Number(this.form.autoRunIntervalMinutes || AUTO_SYNC_DEFAULT_INTERVAL_MINUTES),
          boardId: this.form.boardId || null,
          stageId: this.form.stageId || null,
        });
        return;
      }
      this.$emit("save", {
        ...this.form,
        aiProfileKey: this.selectedPrompt?.profile_key || this.form.aiProfileKey,
        matchFields: (this.form.matchFields || []).map((field) => ({ ...field })),
        targetFields: Array.isArray(this.form.targetFields)
          ? this.form.targetFields
              .map((field) => (typeof field === "object" && field !== null ? field.value : field))
              .filter(Boolean)
          : [],
      });
    },
    async fetchLeadBoards() {
      this.loadingLeadBoards = true;
      this.leadBoardLoadError = "";
      try {
        const { data } = await axios.get("/ncrm/leads/boards");
        this.leadBoards = Array.isArray(data?.items)
          ? data.items.filter((board) => board.is_active !== false && board.is_active !== 0)
          : [];
        if (this.form.boardId) await this.fetchLeadStages(this.form.boardId);
      } catch (err) {
        this.leadBoards = [];
        this.leadBoardLoadError =
          err?.response?.data?.error || this.$t("ncrm.workflows.nodeSettings.leadBoard.loadBoardsError");
      } finally {
        this.loadingLeadBoards = false;
      }
    },
    async fetchLeadStages(boardId = this.form.boardId) {
      const normalizedBoardId = Number(boardId);
      if (!Number.isFinite(normalizedBoardId) || normalizedBoardId <= 0) {
        this.leadStages = [];
        this.form.stageId = null;
        return;
      }
      this.loadingLeadStages = true;
      this.leadBoardLoadError = "";
      try {
        const { data } = await axios.get(`/ncrm/leads/boards/${normalizedBoardId}/stages`);
        this.leadStages = Array.isArray(data?.items)
          ? data.items.filter((stage) => stage.is_active !== false && stage.is_active !== 0)
          : [];
        if (this.form.stageId && !this.leadStages.some((stage) => Number(stage.id) === Number(this.form.stageId))) {
          this.form.stageId = null;
        }
      } catch (err) {
        this.leadStages = [];
        this.form.stageId = null;
        this.leadBoardLoadError =
          err?.response?.data?.error || this.$t("ncrm.workflows.nodeSettings.leadBoard.loadStagesError");
      } finally {
        this.loadingLeadStages = false;
      }
    },
  },
};
</script>
